// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import { EditIcon, TrashIcon } from "src/assets/icons";

// Styles
import { useTheme } from "@mui/material/styles";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import { DryEyeProfileType } from "src/services/clinics/types";

// Utils
import {
  formatLensPrice,
  getDryEyesTooltipText,
} from "src/utils/exams/lensSimulationHelper";

type DryEyeDescriptionType = {
  isUpdating: boolean;
  profileData: DryEyeProfileType;
  openDeleteModal: () => void;
  openEditModal: () => void;
};

const DryEyeDescription = ({
  isUpdating,
  profileData,
  openDeleteModal,
  openEditModal,
}: DryEyeDescriptionType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, descriptionBackground } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const tooltipText = useMemo(
    () => getDryEyesTooltipText(profileData, t),
    [profileData, t]
  );

  // check if edit button should be hidden
  const hideEditButton = useMemo(
    () => !clinicInfo?.licensing?.canUseDryEyeSimulations,
    [clinicInfo?.licensing?.canUseDryEyeSimulations]
  );

  const formattedPrice = useMemo(
    () =>
      formatLensPrice(
        profileData.price,
        clinicInfo?.settings?.currencySymbol,
        clinicInfo?.settings?.currencyCode
      ),
    [
      clinicInfo?.settings?.currencyCode,
      clinicInfo?.settings?.currencySymbol,
      profileData.price,
    ]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: descriptionBackground,
        color: onSurface,
        p: "12px",
        borderRadius: "4px",
        gap: "4px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TitleWithTooltip
            title={profileData?.name}
            tooltip={
              <div style={{ whiteSpace: "pre-line" }}>{tooltipText}</div>
            }
            titleStyle={{
              fontWeight: 600,
              fontSize: "14px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            tooltipStyle={{
              color: onSurface,
            }}
          />
        </Box>

        <Box display="flex" gap="8px" flexShrink={0}>
          <Tooltip title={t("Edit Lens Option")}>
            <IconButton
              sx={{
                color: onSurface,
                padding: 0,
                display: hideEditButton ? "none" : "flex",
              }}
              disabled={isUpdating}
              onClick={openEditModal}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Delete Lens Option")}>
            <IconButton
              sx={{
                color: onSurface,
                padding: 0,
              }}
              disabled={isUpdating}
              onClick={openDeleteModal}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" gap="8px">
        <Typography fontSize="12px">
          {t("Severity")}: {t(profileData.severityType)}
        </Typography>
        <Tooltip title={formattedPrice}>
          <Typography
            fontSize="12px"
            textAlign="right"
            sx={{
              display: "block",
              maxWidth: "30%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formattedPrice}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DryEyeDescription;
